<script setup>
import { ref, inject } from 'vue';
import { useStaffStore } from '@/stores/staff';
import { storeToRefs } from 'pinia';
import { router } from '@inertiajs/vue3';

const { search } = useStaffStore();
const { all } = storeToRefs(useStaffStore());

const params = ref({
    term: '',
    status: null,
    expirationStatus: null,
    missingMandatoryRequirements: null,
    page: 1,
    sort_by: null,
    per_page: 10,
    role: null,
});

const searchStaff = () => {
    search({
        ...params.value,
    });
};

const modals = inject('modals');

const clearSearch = (field) => {
    params[field] = null;
    searchStaff();
};

const tableUpdate = ({ page, sortBy, itemsPerPage }) => {
    Object.assign(params.value, { page, sort_by: sortBy, per_page: itemsPerPage });
    searchStaff();
};

const openModal = (modal, mode = 'Create', staff = null) => {
    modals.show(modal, {
        props: staff ? {
            staff: staff,
            mode: mode
        } : {
            mode: mode,
        }
    }).then(() => {
        searchStaff();
    });
};

const accessStaff = (staff) => {
    router.visit(`/staff/${staff.hash}/details`);
};

import { useOptionsStore } from '@/stores/options';
const { roles } = storeToRefs(useOptionsStore());

</script>

<template>
<MainContentLayout>
    <template #breadcrumb><BreadCrumb /></template>
    <template #title>{{ $t('label.staff_management') }}</template>
    <template #actions>
        <StyledButton @click="openModal('StaffModal')"><FontAwesomeIcon icon="plus" class="mr-1" />
            {{  $t('label.new_staff') }}
        </StyledButton>
    </template>
    <div class="grid lg:grid-cols-5 md:gap-4 md:mb-4">
        <StyledSearch
            v-model="params.term"
            v-debounce:500ms="searchStaff"
            name="search"
            :label="$t('label.search')"
            :debounce-events="['input']"
            @click:clear="clearSearch($event, 'term')" />
        <StyledDropdown
            v-model="params.status"
            clearable
            :placeholder="$t('label.status')"
            :options="[
                { label: $t('label.active'), value: true },
                { label: $t('label.inactive'), value: false },
            ]"
            name="status"
            @click:clear="clearSearch($event, 'status')"
            @input="searchStaff" />
        <StyledDropdown
            v-model="params.role"
            clearable
            :placeholder="$t('label.role')"
            :options="roles"
            name="role"
            @click:clear="clearSearch($event, 'role')"
            @input="searchStaff" />
        <StyledDropdown
            v-model="params.expirationStatus" 
            clearable
            :placeholder="$t('label.expired_requirement_status')" 
            :options="[
                { label: $t('label.has_expired_requirements'), value: true },
                { label: $t('label.does_not_have_expired_requirements'), value: false },
            ]"
            @click:clear="clearSearch($event, 'expirationStatus')"
            @input="searchStaff" />

        <StyledDropdown
            v-model="params.missingMandatoryRequirements" 
            clearable
            :placeholder="$t('label.missing_mandatory_requirements')" 
            :options="[
                { label: $t('label.is_missing_mandatory_requirements'), value: true },
                { label: $t('label.not_missing_mandatory_requirements'), value: false },
            ]"
            @click:clear="clearSearch($event, 'missingMandatoryRequirements')"
            @input="searchStaff" />
    </div>
    <DataTable
        v-if="all"
        :data="all"
        :headers="[
            { title: $t('label.name'), key: 'full_name', value: item => `${item.first_name} ${item.last_name}`, sortable: false },
            { title: $t('label.email'), key: 'email', value: item => item.user.email, sortable: false },
            { title: $t('label.phone'), key: 'phone', value: item => item.phone, sortable: false },
            { title: $t('label.has_expired_requirements'), key: 'has_expired_requirements', value: item => item.has_expired_requirements ? $t('label.yes') : $t('label.no'), sortable: false},
            { title: $t('label.missing_mandatory_requirements'), key: 'missing_mandatory_requirements', value: item => item.missing_mandatory_requirements ? $t('label.yes') : $t('label.no'), sortable: false},
            { title: $t('label.type'), key: 'type', value: item => item.user.roles.length ? item.user.roles[0].display_name : '', sortable: false},
            { title: $t('label.status'), key: 'status', value: item => item.user.is_active ? $t('label.active') : $t('label.inactive'), sortable: false },
        ]"
        :actions="[
            {
                title: $t('label.access'),
                icon: 'mdi-arrow-right-circle-outline',
                action: (item) => accessStaff(item),
            },
        ]"
        @table-update="tableUpdate" />
</MainContentLayout>
</template>
